// React
import React from 'react'
import { findDOMNode } from 'react-dom'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import FocalImage from 'ui/components/FocalImage'
import ProductPrice from './product_price'

// Libraries
import _capitalize from 'lodash/capitalize'
import _get from 'lodash/get'

// Shared
import { withTranslation } from 'shared/utils/withTranslation'
import ReactCSSTransitionGroup from 'shared/lib/react_css_transition_group'
import quickviewActions from 'store/v2/redux/actions/quickview'

export class Product extends React.Component {
  static displayName = 'Product'

  static propTypes = {
    loading: PropTypes.bool,
    id: PropTypes.string,
    item: PropTypes.object,
    photo: PropTypes.object,
    useAvailability: PropTypes.bool,
    hideProductAvailabilityQuantities: PropTypes.bool,
    hasDates: PropTypes.string,
    hasPrices: PropTypes.any,
    onClick: PropTypes.func,
    openQuickview: PropTypes.func,
    t: PropTypes.func
  }

  componentDidMount = () => {
    this.setWidth()

    window.addEventListener('resize', this.setWidth, false)
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.setWidth)
  }

  findAncestor = (el, cls) => {
    while ((el = el.parentElement) && !el.classList.contains(cls));

    return el
  }

  setWidth = () => {
    // eslint-disable-next-line react/no-find-dom-node
    const node = this.findAncestor(findDOMNode(this), 'booqable-product')
    const container = node?.parentNode

    if (!node || !container) return

    const width = container.offsetWidth
    let maxPerRow = Math.floor(width / 280)

    // Max of 6 per row
    maxPerRow = Math.max(1, Math.min(maxPerRow, 6))

    return (node.style.width = 100 / maxPerRow + '%')
  }

  renderLoading = () => {
    const { t } = this.props

    return (
      <div className="booqable-product-wrapper">
        <div className="booqable-product-inner">
          <ReactCSSTransitionGroup transitionName="fade" transitionEnterTimeout={100} transitionLeaveTimeout={100}>
            <span className="bq-no-photo">
              <i className="bq-no-photo bq-icon-image fas fa-image" />
            </span>
          </ReactCSSTransitionGroup>
          <div className="bq-details">
            <div className="bq-product-title-wrapper bq-has-no-prices">
              <div className="bq-product-name">
                <span className="fake-text">{t('user.store.product_placeholder')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderContent = () => {
    const { id, item, photo, onClick, openQuickview, useAvailability, hideProductAvailabilityQuantities, hasDates, hasPrices, t } = this.props

    const preview = _get(photo, 'preview')
    const imageSrc = _get(photo, 'large_url') || item.photo_url
    const imageCoordinates = _get(photo, 'coordinates')

    return (
      <div className="booqable-product-wrapper">
        <div className="booqable-product-inner" onClick={onClick.bind(null, id)}>
          <ReactCSSTransitionGroup transitionName="fade" transitionEnterTimeout={100} transitionLeaveTimeout={100}>
            {imageSrc && (
              <FocalImage
                height="250px"
                preview={preview}
                url={imageSrc}
                coordinates={imageCoordinates}
                maintainAspect
              />
            )}
            {!imageSrc && (
              <span className="bq-no-photo">
                <i className="bq-no-photo bq-icon-image fas fa-image" />
              </span>
            )}
          </ReactCSSTransitionGroup>
          <div className="bq-details">
            {useAvailability && hasDates && (
              <ReactCSSTransitionGroup transitionName="fade" transitionEnterTimeout={100} transitionLeaveTimeout={100}>
                {item.available_quantity === null && (
                  <span className="bq-branded bq-status bq-available">{_capitalize(t('user.store.available'))}</span>
                )}
                {item.available_quantity === 0 && (
                  <span className="bq-status bq-unavailable">{_capitalize(t('user.store.unavailable'))}</span>
                )}
                {item.available_quantity > 0 && (
                  hideProductAvailabilityQuantities ?
                    (
                      <span className="bq-branded bq-status bq-available">
                        {_capitalize(t('user.store.available'))}
                      </span>
                    ) :
                    (
                      <span className="bq-branded bq-status bq-available">
                        <span className="bq-status-quantity">{item.available_quantity} </span>
                        {t('user.store.available')}
                      </span>
                    )
                )}
              </ReactCSSTransitionGroup>
            )}
            <button
              className="bq-button bq-branded"
              data-link="true"
              onClick={openQuickview.bind(null, id)}
              style={{ display: 'none' }}
            >
              <i data-link="true" className="bq-icon-cart fas fa-shopping-cart bq-link bq-branded-font-color" />
            </button>
            <div
              className={`bq-product-title-wrapper ${
                hasPrices || typeof hasPrices === 'number' ? 'bq-has-prices' : 'bq-has-no-prices'
              }`}
            >
              <div title={item.name} className="bq-product-name">
                {item.name}
              </div>
            </div>
            {item && (
              <ProductPrice
                chargeLabel={item.charge_label}
                priceInCents={item.price_each_in_cents}
                priceType={item.price_type}
                productType={item.product_type}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  render () {
    const { loading } = this.props

    return loading ? this.renderLoading() : this.renderContent()
  }
}

const mapStateToProps = (state, props) => {
  const showPrices = state.settings.get('store.use_prices')
  const item = state.orm.items.getById(props.id)
  const cart = state.orm.carts.getById(state.client.cartId)
  const photo = item && state.orm.photos.getById(item.photo_id)

  return {
    loading: !item,
    translations: state.client.get('translations').toJS(),
    item,
    photo,
    useAvailability: state.settings.get('store.use_availability'),
    hideProductAvailabilityQuantities: state.settings.get('store.hide_product_availability_quantities'),
    hasDates: cart?.starts_at && cart?.stops_at,
    hasPrices: showPrices && item?.price_each_in_cents
  }
}

const mapDispatchToProps = (_, props) => {
  const openQuickview = (id) => quickviewActions.setItem(id)

  return {
    openQuickview,
    onClick: (id, e) => {
      if (props.link && !e.target.getAttribute('data-link')) {
        return (window.location = props.link)
      } else {
        return openQuickview(id)
      }
    }
  }
}

export default compose(reduxConnect(mapStateToProps, mapDispatchToProps), withTranslation('user'))(Product)
